import { ReactRenderer } from '@tiptap/react'
import tippy from 'tippy.js'
import { SuggestionKeyDownProps } from '@tiptap/suggestion'
import MentionList from './MentionList'

// Define the suggestion configuration for both @ and @@ triggers
export const createMentionSuggestion = (
    getUsersFn: () => any[],
    getModelsFn: () => any[],
    setAgentModel?: (model: string | null) => void,
) => {
    return {
        items: ({ query, editor }: { query: string; editor: any }) => {
            // Get fresh data on each call
            const users = getUsersFn()
            const models = getModelsFn()

            const { state } = editor
            const { selection } = state
            const { $from } = selection

            // Get more context to better detect triggers
            const textBefore = state.doc.textBetween(Math.max(0, $from.pos - 30), $from.pos, '', '')
            console.log(
                '[MentionSuggestion] Full textBefore:',
                `"${textBefore}"`,
                'query:',
                `"${query}"`,
            )

            // More precise pattern matching for @@ vs @
            // Look for @@ pattern at word boundaries or start/space
            // Include hyphens, underscores, and dots in username patterns
            const doubleAtPattern = /(?:^|[\s\n])@@([a-zA-Z0-9\-_.]*)$/
            const singleAtPattern = /(?:^|[\s\n])@([a-zA-Z0-9\-_.]*)$/

            const doubleAtMatch = textBefore.match(doubleAtPattern)
            const singleAtMatch = textBefore.match(singleAtPattern)

            // Determine trigger type based on matches
            let triggerType = null
            let actualQuery = query

            // Special case: if query starts with @, it means we're transitioning from @ to @@
            if (query.startsWith('@')) {
                triggerType = 'model'
                actualQuery = query.substring(1) // Remove the @ from query
                console.log(
                    '[MentionSuggestion] Query-based model trigger detected (@ → @@), query:',
                    actualQuery,
                )
            } else if (doubleAtMatch && !textBefore.includes('@@@')) {
                // Model trigger @@
                triggerType = 'model'
                actualQuery = doubleAtMatch[1] || query
                console.log(
                    '[MentionSuggestion] Pattern-based model trigger detected, query:',
                    actualQuery,
                )
            } else if (singleAtMatch && !doubleAtMatch && !query.startsWith('@')) {
                // User trigger @ (but not part of @@, and query doesn't start with @)
                triggerType = 'user'
                actualQuery = singleAtMatch[1] || query
                console.log('[MentionSuggestion] User trigger detected, query:', actualQuery)
            }

            // Alternative detection: check the immediate characters before cursor
            if (!triggerType) {
                const lastChars = textBefore.slice(-10)
                if (lastChars.includes('@@')) {
                    const afterDoubleAt = lastChars.split('@@').pop() || ''
                    if (afterDoubleAt === query || afterDoubleAt.includes(query)) {
                        triggerType = 'model'
                        actualQuery = afterDoubleAt
                        console.log(
                            '[MentionSuggestion] Alternative model detection, query:',
                            actualQuery,
                        )
                    }
                } else if (lastChars.includes('@') && !lastChars.includes('@@')) {
                    const afterAt = lastChars.split('@').pop() || ''
                    if (afterAt === query || afterAt.includes(query)) {
                        triggerType = 'user'
                        actualQuery = afterAt
                        console.log(
                            '[MentionSuggestion] Alternative user detection, query:',
                            actualQuery,
                        )
                    }
                }
            }

            if (triggerType === 'model') {
                console.log(
                    '[MentionSuggestion] Processing models:',
                    models.length,
                    'models available',
                )
                console.log('[MentionSuggestion] Sample model:', models[0])
                console.log('[MentionSuggestion] Query for models:', `"${actualQuery}"`)

                // If no query, show all models
                if (!actualQuery || actualQuery.trim() === '') {
                    const allModels = models.slice(0, 10).map(model => ({
                        ...model,
                        type: 'model',
                        denotationChar: '@@',
                        triggerType: 'model',
                    }))

                    console.log(
                        '[MentionSuggestion] Returning all models (no query):',
                        allModels.length,
                    )
                    return allModels
                }

                // Filter models with flexible property matching
                const filteredModels = models
                    .filter(model => {
                        // Try multiple possible property names for model content
                        const content = (
                            model.content ||
                            model.name ||
                            model.id ||
                            model.value ||
                            ''
                        ).toLowerCase()
                        const searchName = (
                            model.searchableName ||
                            model.searchName ||
                            ''
                        ).toLowerCase()
                        const queryLower = actualQuery.toLowerCase()

                        const matches =
                            content.includes(queryLower) || searchName.includes(queryLower)
                        console.log('[MentionSuggestion] Model filter:', {
                            model: model.content || model.name || model.id,
                            content,
                            searchName,
                            queryLower,
                            matches,
                        })

                        return matches
                    })
                    .sort((a, b) => {
                        // Sort to prioritize exact matches
                        const queryLower = actualQuery.toLowerCase()

                        // Get content for comparison
                        const aContent = (
                            a.content ||
                            a.name ||
                            a.id ||
                            a.value ||
                            ''
                        ).toLowerCase()
                        const bContent = (
                            b.content ||
                            b.name ||
                            b.id ||
                            b.value ||
                            ''
                        ).toLowerCase()

                        // Check for exact matches
                        const aExact = aContent === queryLower
                        const bExact = bContent === queryLower

                        // Check if starts with query
                        const aStarts = aContent.startsWith(queryLower)
                        const bStarts = bContent.startsWith(queryLower)

                        // Sort order: exact match > starts with > contains
                        if (aExact && !bExact) return -1
                        if (!aExact && bExact) return 1
                        if (aStarts && !bStarts) return -1
                        if (!aStarts && bStarts) return 1

                        // If both are similar, maintain original order
                        return 0
                    })
                    .slice(0, 10)
                    .map(model => ({
                        ...model,
                        type: 'model',
                        denotationChar: '@@',
                        triggerType: 'model',
                    }))

                console.log('[MentionSuggestion] Returning filtered models:', filteredModels.length)
                return filteredModels
            } else if (triggerType === 'user') {
                console.log(
                    '[MentionSuggestion] Processing users:',
                    users.length,
                    'users available',
                )
                console.log('[MentionSuggestion] Sample user:', users[0])
                console.log('[MentionSuggestion] Query for users:', `"${actualQuery}"`)

                // Filter users
                const filteredUsers = users
                    .filter(user => {
                        const userName = user.content?.startsWith('@')
                            ? user.content.substring(1).toLowerCase()
                            : user.content?.toLowerCase() || ''
                        const queryLower = actualQuery.toLowerCase()
                        const matches = userName.includes(queryLower)

                        console.log('[MentionSuggestion] User filter:', {
                            user: user.content,
                            userName,
                            queryLower,
                            matches,
                        })

                        return matches
                    })
                    .sort((a, b) => {
                        // Sort to prioritize exact matches
                        const queryLower = actualQuery.toLowerCase()

                        // Get username for comparison (remove @ if present)
                        const aUserName = a.content?.startsWith('@')
                            ? a.content.substring(1).toLowerCase()
                            : a.content?.toLowerCase() || ''
                        const bUserName = b.content?.startsWith('@')
                            ? b.content.substring(1).toLowerCase()
                            : b.content?.toLowerCase() || ''

                        // Check for exact matches
                        const aExact = aUserName === queryLower
                        const bExact = bUserName === queryLower

                        // Check if starts with query
                        const aStarts = aUserName.startsWith(queryLower)
                        const bStarts = bUserName.startsWith(queryLower)

                        // Sort order: exact match > starts with > contains
                        if (aExact && !bExact) return -1
                        if (!aExact && bExact) return 1
                        if (aStarts && !bStarts) return -1
                        if (!aStarts && bStarts) return 1

                        // If both are similar, maintain original order
                        return 0
                    })
                    .slice(0, 10)
                    .map(user => ({
                        ...user,
                        type: 'user',
                        denotationChar: '@',
                        triggerType: 'user',
                    }))

                console.log('[MentionSuggestion] Returning filtered users:', filteredUsers.length)
                return filteredUsers
            }

            console.log(
                '[MentionSuggestion] No trigger detected - textBefore:',
                `"${textBefore}"`,
                'query:',
                `"${query}"`,
            )
            console.log('[MentionSuggestion] Available data:', {
                users: users?.length,
                models: models?.length,
            })
            return []
        },

        render: () => {
            let component: ReactRenderer
            let popup: any

            return {
                onStart: (props: any) => {
                    console.log('[MentionSuggestion] onStart:', props)
                    component = new ReactRenderer(MentionList, {
                        props,
                        editor: props.editor,
                    })

                    if (!props.clientRect) {
                        return
                    }

                    popup = tippy('body', {
                        getReferenceClientRect: props.clientRect,
                        appendTo: () => document.body,
                        content: component.element,
                        showOnCreate: true,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'bottom-start',
                        zIndex: 10000,
                    })
                },

                onUpdate(props: any) {
                    console.log('[MentionSuggestion] onUpdate:', props)
                    component?.updateProps(props)

                    if (!props.clientRect) {
                        return
                    }

                    popup?.[0]?.setProps({
                        getReferenceClientRect: props.clientRect,
                    })
                },

                onKeyDown(props: SuggestionKeyDownProps) {
                    if (props.event.key === 'Escape') {
                        popup?.[0]?.hide()
                        return true
                    }

                    return (component?.ref as any)?.onKeyDown?.(props) || false
                },

                onExit() {
                    console.log('[MentionSuggestion] onExit')
                    popup?.[0]?.destroy()
                    component?.destroy()
                },
            }
        },

        // Handle both @ and @@ triggers
        char: '@',

        // Allow spaces in suggestions
        allowSpaces: true,

        // Custom character detection to handle @@
        allowedPrefixes: [' ', '\n', null],

        startOfLine: false,

        // Custom command to handle insertion
        command: ({ editor, range, props }: any) => {
            console.log('[MentionSuggestion] Command called:', { range, props })
            const { triggerType } = props

            // For model mentions, we need special handling
            if (triggerType === 'model') {
                // Use a custom command that handles everything in one transaction
                editor
                    .chain()
                    .focus()
                    .command(({ tr, state, dispatch }) => {
                        const { doc, selection } = state
                        const { $from } = selection

                        // Find the trigger text position
                        const textBefore = doc.textBetween(
                            Math.max(0, $from.pos - 30),
                            $from.pos,
                            '',
                            '',
                        )
                        const doubleAtIndex = textBefore.lastIndexOf('@@')

                        if (doubleAtIndex === -1) {
                            console.warn('[MentionSuggestion] Could not find @@ trigger')
                            return false
                        }

                        const triggerStart = $from.pos - (textBefore.length - doubleAtIndex)
                        const triggerEnd = $from.pos

                        // Find existing model mentions
                        const existingMentions: Array<{ pos: number; id: string; size: number }> =
                            []
                        doc.descendants((node, pos) => {
                            if (
                                node.type.name === 'mention' &&
                                node.attrs?.denotationChar === '@@'
                            ) {
                                existingMentions.push({
                                    pos,
                                    id: node.attrs.id,
                                    size: node.nodeSize,
                                })
                            }
                        })

                        console.log(
                            '[MentionSuggestion] Found existing mentions:',
                            existingMentions,
                        )

                        // Check if it's the same model
                        if (existingMentions.length > 0 && existingMentions[0].id === props.id) {
                            console.log(
                                '[MentionSuggestion] Same model exists, just removing trigger',
                            )
                            // Just delete the trigger text
                            tr.delete(triggerStart, triggerEnd)
                            if (dispatch) dispatch(tr)
                            return true
                        }

                        // Start with deleting the trigger text
                        tr.delete(triggerStart, triggerEnd)

                        // Remove existing model mentions (adjust positions after trigger deletion)
                        existingMentions.forEach(mention => {
                            let adjustedPos = mention.pos
                            // If mention comes after trigger, adjust for deleted trigger length
                            if (mention.pos > triggerStart) {
                                adjustedPos -= triggerEnd - triggerStart
                            }

                            // Check if node still exists at adjusted position
                            const nodeAtPos = tr.doc.nodeAt(adjustedPos)
                            if (nodeAtPos && nodeAtPos.type.name === 'mention') {
                                console.log(
                                    '[MentionSuggestion] Removing mention at adjusted pos:',
                                    adjustedPos,
                                )
                                tr.delete(adjustedPos, adjustedPos + mention.size)
                            }
                        })

                        // Create and insert the new mention node
                        const mentionNode = state.schema.nodes.mention.create({
                            id: props.id,
                            label: props.content,
                            type: props.type,
                            denotationChar: props.denotationChar,
                            content: props.content,
                            link: props.link,
                        })

                        // Insert at the trigger position
                        tr.insert(triggerStart, mentionNode)

                        // Add a space after the mention
                        tr.insert(triggerStart + mentionNode.nodeSize, state.schema.text(' '))

                        // Set cursor position after mention + space to fix Android issue
                        const cursorPos = triggerStart + mentionNode.nodeSize + 1
                        tr.setSelection(state.selection.constructor.near(tr.doc.resolve(cursorPos)))

                        // Dispatch the transaction
                        if (dispatch) {
                            dispatch(tr)
                        }

                        return true
                    })
                    .run()

                // Update the agent model state
                if (setAgentModel && props.id) {
                    console.log('[MentionSuggestion] Setting agent model:', props.id)
                    setAgentModel(props.id)
                }
            } else {
                // For user mentions, use the standard flow
                // Get current text to determine correct range
                const { state } = editor
                const { selection } = state
                const { $from } = selection
                const textBefore = state.doc.textBetween(
                    Math.max(0, $from.pos - 30),
                    $from.pos,
                    '',
                    '',
                )

                let adjustedRange = range

                // For single @ triggers
                const singleAtIndex = textBefore.lastIndexOf('@')
                if (singleAtIndex !== -1 && !textBefore.endsWith('@@')) {
                    const actualStart = $from.pos - (textBefore.length - singleAtIndex)
                    adjustedRange = {
                        from: actualStart,
                        to: range.to,
                    }
                }

                console.log(
                    '[MentionSuggestion] Using adjusted range for user mention:',
                    adjustedRange,
                )

                // Insert the user mention
                editor
                    .chain()
                    .focus()
                    .deleteRange(adjustedRange)
                    .insertMention({
                        id: props.id,
                        label: props.content,
                        type: props.type,
                        denotationChar: props.denotationChar,
                        content: props.content,
                        link: props.link,
                    })
                    .run()

                // Add space after mention (separate command to fix Android cursor positioning)
                editor.commands.insertContent(' ')
            }
        },
    }
}
